import React, { useEffect, useState } from "react"

import { NotificationContainer } from "react-notifications"

import {
  ClipboardDocumentCheckIcon,
  InformationCircleIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

import WithLoadingIndicator from "~tailwindui/Basics/WithLoadingIndicator"

import { gql, useQuery } from "hooks/urql"

import LocalTime from "components/LocalTime"
import Pagination from "components/Pagination"

import DeleteReferralConfirmationModal from "./DeleteConfirmationModal"
import DetailsModal from "./DetailsModal"
import EditReferralModal from "./EditModal"
import NewReferralModal from "./NewModal"

import "react-notifications/lib/notifications.css"

export type ReferralType = {
  id: string
  userType: "parent" | "student"
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  gradeId: string
  grade?: {
    id: string
    name: string
  }
}

const ReferralsIndex = () => {
  const [page, setPage] = useState(1)
  const [copiedLink, setCopiedLink] = useState<string | null>()
  const [viewingReferral, setViewingReferral] = useState<ReferralType | null>()
  const [editingReferral, setEditingReferral] = useState<ReferralType | null>()
  const [deletingReferral, setDeletingReferral] =
    useState<ReferralType | null>()

  const [result, refetchQuery] = useQuery({
    query: referralsQuery,
    variables: { page },
  })

  useEffect(() => {
    refetchQuery({ requestPolicy: "network-only" })
  }, [refetchQuery, editingReferral, deletingReferral])

  const addToClipboard = (linkText: string) => {
    navigator.clipboard.writeText(linkText)
    setCopiedLink(linkText)
    setTimeout(() => setCopiedLink(null), 2000)
  }

  return (
    <>
      <DetailsModal
        referral={viewingReferral}
        closeModal={() => setViewingReferral(null)}
      />
      <DeleteReferralConfirmationModal
        referral={deletingReferral}
        closeModal={() => setDeletingReferral(null)}
      />
      <EditReferralModal
        referral={editingReferral}
        closeModal={() => setEditingReferral(null)}
      />
      <NotificationContainer />

      <NewReferralModal
        afterClose={() => refetchQuery({ requestPolicy: "network-only" })}
      />
      <h3>Referrals</h3>

      <WithLoadingIndicator result={result}>
        {({ data: { referrals } }) => (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>User Type</th>
                  <th>Name</th>
                  <th>Registration Link</th>
                  <th>Referred By</th>
                  <th>Referred On</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {referrals.data.map(referral => (
                  <tr key={referral.id}>
                    <td>
                      {referral.recipient ? (
                        "Registered"
                      ) : (
                        <span className="font-semibold">Pending</span>
                      )}
                    </td>
                    <td>{referral.userType}</td>
                    <td>
                      {referral.firstName} {referral.lastName}
                    </td>
                    <td>
                      {copiedLink === referral.link ? (
                        <div className="flex items-center text-green-500">
                          <ClipboardDocumentCheckIcon className="mr-2 h-6 w-6" />
                          <span className="font-medium italic text-green-600">
                            Link copied!
                          </span>
                        </div>
                      ) : (
                        <button
                          className="cursor-pointer border-none bg-transparent hover:text-sky-500"
                          onClick={() => addToClipboard(referral.link)}
                        >
                          Click to copy
                        </button>
                      )}
                    </td>
                    <td>
                      <a href={referral.referrer.showPath}>
                        {referral.referrer.fullName}
                      </a>
                    </td>
                    <td>
                      <LocalTime omitTime timestamp={referral.createdAt} />
                    </td>
                    <td className="sm:space-x-4">
                      <button
                        type="button"
                        className="cusor-pointer rounded-[0.3rem] border-solid border-sky-500 bg-transparent p-1 focus:outline-sky-700"
                        onClick={() => setViewingReferral(referral)}
                      >
                        <InformationCircleIcon className="inline-block h-6 w-6 cursor-pointer align-middle text-sky-500" />
                      </button>
                      <button
                        type="button"
                        className="cusor-pointer rounded-[0.3rem] border-solid border-green-500 bg-transparent p-1 focus:outline-green-700"
                        onClick={() => setEditingReferral(referral)}
                      >
                        <PencilIcon className="inline-block h-6 w-6 cursor-pointer align-middle text-green-500" />
                      </button>
                      {!referral.recipient && (
                        <button
                          type="button"
                          className="cusor-pointer rounded-[0.3rem] border-solid border-red-500 bg-transparent p-1 focus:outline-red-700"
                          onClick={() => setDeletingReferral(referral)}
                        >
                          <XMarkIcon className="inline-block h-6 w-6 cursor-pointer align-middle text-red-500" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage={page}
              changePage={setPage}
              totalPages={referrals.totalPages}
            />
          </>
        )}
      </WithLoadingIndicator>
    </>
  )
}

const referralsQuery = gql`
  query Referrals($page: Int) {
    referrals(page: $page) {
      currentPage
      totalPages
      data {
        id
        createdAt
        userType
        link
        firstName
        lastName
        phoneNumber
        email
        grade {
          id
          name
        }
        referrer {
          id
          fullName
          showPath
        }
        recipient {
          id
          fullName
          showPath
        }
      }
    }
  }
`

export default ReferralsIndex
